import { useAccountModal, useChainModal, useConnectModal } from '@rainbow-me/rainbowkit';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import {
  createContext, ReactNode, useContext, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Address } from 'viem';
import {
  useAccount, useDisconnect, useSignMessage, useSwitchChain,
} from 'wagmi';
import { wagmiConfig } from '../../configs/wagmi-config';
import {
  AVAILABLE_CHAINS, DEFAULT_CHAIN_ID, isSupportedChainId, MAP_CHAINS_ICONS, MAP_CHAINS_NAMES,
} from '../../constants/chains';
import { useErrorsMap } from '../../hooks/use-errors-map';
import { usePromiseModal } from '../../hooks/use-promise-modal';
import { AgeCheckApi } from '../../services/age-check-api';
import { DashboardApi } from '../../services/dashboard-api';
import { WalletLoginApi } from '../../services/wallet-login-api';
import { AgeVerificationError } from '../../types/errors';
import { RedirectUrlResponse } from '../../types/redirect-url';
import { mapErrorMessage } from '../../utils/map-error-message';
import { useConfirmPopUp } from '../../utils/use-confirm-pop-up';
import { useDebounceValue } from '../../utils/use-debounce-value';
import { useUserInfo } from '../user-info';
import { AgeVerificationPopup } from './components/AgeVerificationPopup';
import { SignTermsPopup } from './components/SignTermsPopup';
import { WalletActionProcess } from './components/WalletActionProcess';
import { useCloseConnectModalController } from './hooks/use-close-connect-modal-controller';
import { waitAddress } from './hooks/wait-address';

type ChainInfo = {
  id: number;
  name: string;
  icon: string;
};

type WalletsContextProps = {
  connectedAddress?: Address;
  chainId?: number;
  chain?: ChainInfo;
  mainAddress?: Address;
  addresses?: string[];
  isConnected: boolean;
  isConnecting: boolean;
  isSigningPending: boolean;
  isWalletMatched: boolean;
  isChainIdMatched: boolean;
  isChainSupported: boolean;
  switchChain: (chainId?: number) => Promise<void>;
  disconnect: () => Promise<void>;
  openAccountModal: VoidFunction;
  openChainModal: VoidFunction;
  connectWallet: UseMutationResult<void, Error>;
  signInWallet: UseMutationResult<RedirectUrlResponse, Error>,
  signUpWallet: UseMutationResult<RedirectUrlResponse, Error>,
  setMainAddress: UseMutationResult<void, Error, Address>;
  unlinkAddress: UseMutationResult<void, Error, Address>;
}

const WalletsContext = createContext<WalletsContextProps | undefined>(undefined);

export const WalletsProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { data: userInfo, refetch: refetchUserInfo, isLoading: isLoadingUserInfo } = useUserInfo();

  const {
    address: connectedAddress,
    isConnecting,
    isConnected,
    chainId,
  } = useAccount();
  const isChainSupported = useMemo(() => !!AVAILABLE_CHAINS.find(({ id }) => id === chainId), [chainId]);
  const { openConnectModal = () => { }, connectModalOpen } = useConnectModal();
  const { openAccountModal = () => { } } = useAccountModal();
  const { openChainModal = () => { } } = useChainModal();
  const debounceConnectModalOpen = useDebounceValue(connectModalOpen, 100);
  const {
    open: isAgeVerificationModalOpen,
    waitResult: ageVerificationResult,
    resolve,
  } = usePromiseModal<boolean | undefined>();

  const { disconnectAsync } = useDisconnect();
  const { switchChainAsync } = useSwitchChain();
  const isChainIdMatched = chainId === DEFAULT_CHAIN_ID;
  const isWalletMatched = useMemo(
    () => (connectedAddress && userInfo?.wallets ? userInfo.wallets.includes(connectedAddress) : false),
    [userInfo?.wallets, connectedAddress],
  );
  const [PopUp, confirm] = useConfirmPopUp({ confirmOnly: true, withSound: false });
  const { signMessageAsync, status: signingStatus } = useSignMessage();
  const getCloseModalController = useCloseConnectModalController();

  const errorsMap = useErrorsMap();

  const chain = useMemo<ChainInfo | undefined>(() => {
    if (!isSupportedChainId(chainId)) {
      return undefined;
    }
    return {
      id: chainId,
      name: MAP_CHAINS_NAMES[chainId],
      icon: MAP_CHAINS_ICONS[chainId],
    };
  }, [chainId]);

  const switchChain = async (switchChainId: number = DEFAULT_CHAIN_ID) => {
    try {
      await switchChainAsync({ chainId: switchChainId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);

      await confirm({
        message: mapErrorMessage(e),
        title: 'SWITCH CHAIN',
      });
    }
  };

  const isVerifiedWallet = (address: string) => !!userInfo?.wallets?.find((walletAddress) => walletAddress === address);

  const processingError = (title: string) => async (error: Error) => {
    if (error.name === 'AbortError') {
      return;
    }

    confirm({
      message: mapErrorMessage(error, errorsMap),
      title,
    });

    await disconnectAsync();
  };

  const connectWallet: UseMutationResult<void, Error> = useMutation({
    mutationKey: ['connect', 'wallet'],
    mutationFn: async () => {
      if (!userInfo?.isAgeCheckPassed) {
        const isAgeVerified = await ageVerificationResult();

        if (!isAgeVerified) {
          await disconnectAsync();
          throw new AgeVerificationError();
        }

        await AgeCheckApi.pass();
        await refetchUserInfo();
      }

      openConnectModal();

      const closeModalController = getCloseModalController();
      const address = await waitAddress(wagmiConfig, { signal: closeModalController.signal });

      if (isVerifiedWallet(address)) {
        return;
      }

      const message = await DashboardApi.getWalletMessage(address);
      const signature = await signMessageAsync({ account: address, message });

      await DashboardApi.linkWallet({ wallet: address, data: { signature } });
      await refetchUserInfo();
    },
    onError: processingError(t('walletsContext.walletLink')),
  });

  const signUpWallet: UseMutationResult<RedirectUrlResponse, Error> = useMutation({
    mutationKey: ['signUp', 'wallet'],
    mutationFn: async () => {
      openConnectModal();

      const closeModalController = getCloseModalController();
      const address = await waitAddress(wagmiConfig, { signal: closeModalController.signal });

      const message = await WalletLoginApi.getWalletMessage(address);
      const signature = await signMessageAsync({
        account: address,
        message,
      });

      return WalletLoginApi.signUp(address, { signature });
    },
    onError: processingError(t('walletsContext.signUp')),
    onSuccess: () => refetchUserInfo(),
  });

  const signInWallet: UseMutationResult<RedirectUrlResponse, Error> = useMutation({
    mutationKey: ['signIn', 'wallet'],
    mutationFn: async () => {
      openConnectModal();

      const closeModalController = getCloseModalController();
      const address = await waitAddress(wagmiConfig, { signal: closeModalController.signal });

      const message = await WalletLoginApi.getWalletMessage(address);
      const signature = await signMessageAsync({ account: address, message });
      const signInData = await WalletLoginApi.signIn(address, { signature });

      try {
        const { data: freshUserInfo } = await refetchUserInfo();

        if (freshUserInfo && !freshUserInfo.isAgeCheckPassed) {
          const isAgeVerified = await ageVerificationResult();

          if (isAgeVerified) {
            await AgeCheckApi.pass();
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }

      return signInData;
    },
    onError: processingError(t('walletsContext.signIn')),
  });

  const unlinkAddress: UseMutationResult<void, Error, Address> = useMutation({
    mutationKey: ['unlink', 'address'],
    mutationFn: async (address: Address) => {
      await DashboardApi.unlinkWallet(address);

      if (connectedAddress === address) {
        await disconnectAsync();
      }

      await refetchUserInfo();
    },
    onError: processingError(t('walletsContext.walletUnlink')),
  });

  const setMainAddress: UseMutationResult<void, Error, Address> = useMutation({
    mutationKey: ['set', 'mainAddress'],
    mutationFn: async (address: Address) => {
      await DashboardApi.setMainWallet(address);
      await refetchUserInfo();
    },
    onError: processingError(t('walletsContext.settingMainWallet')),
  });

  const walletActionProcessTitle = useMemo(() => {
    if (signInWallet.isPending) {
      return t('walletsContext.signIn');
    }

    if (signInWallet.isPending) {
      return t('walletsContext.signUp');
    }

    if (setMainAddress.isPending) {
      return t('walletsContext.setMainWallet');
    }

    if (unlinkAddress.isPending) {
      return t('walletsContext.unlinkWallet');
    }

    return t('walletsContext.walletLink');
  }, [
    connectWallet.isPending,
    signInWallet.isPending,
    signUpWallet.isPending,
    setMainAddress.isPending,
    unlinkAddress.isPending,
  ]);

  const isWalletActionModalOpen = useMemo(
    () => (
      connectWallet.isPending
      || signInWallet.isPending
      || signUpWallet.isPending
      || setMainAddress.isPending
      || unlinkAddress.isPending
    )
      && signingStatus !== 'pending'
      && !debounceConnectModalOpen
      && !isAgeVerificationModalOpen,
    [
      connectWallet.isPending,
      signInWallet.isPending,
      signUpWallet.isPending,
      setMainAddress.isPending,
      unlinkAddress.isPending,
      signingStatus,
      debounceConnectModalOpen,
      isAgeVerificationModalOpen,
    ],
  );

  useEffect(() => {
    if (connectWallet.isPending || signInWallet.isPending || signUpWallet.isPending) {
      return;
    }

    if (connectedAddress && !isLoadingUserInfo && !isVerifiedWallet(connectedAddress)) {
      disconnectAsync();
    }
  }, [connectedAddress, connectWallet.isPending, signInWallet.isPending, signUpWallet.isPending, isLoadingUserInfo]);

  const providerValue: WalletsContextProps = useMemo(() => ({
    connectedAddress,
    chainId,
    chain,
    mainAddress: userInfo?.walletAddress ? userInfo.walletAddress : undefined,
    addresses: userInfo?.wallets ?? [],
    isConnected,
    isConnecting,
    isSigningPending: signingStatus === 'pending',
    isWalletMatched,
    isChainIdMatched,
    isChainSupported,
    openAccountModal,
    openChainModal,
    setMainAddress,
    unlinkAddress,
    connectWallet,
    signInWallet,
    signUpWallet,
    disconnect: disconnectAsync,
    switchChain,
  }), [
    userInfo,
    openAccountModal,
    openChainModal,
    connectWallet,
    signInWallet,
    signUpWallet,
    disconnectAsync,
    isConnected,
    isConnecting,
    isConnecting,
    signingStatus,
    chainId,
    chain,
    isWalletMatched,
    isChainIdMatched,
    isChainSupported,
    setMainAddress,
    unlinkAddress,
    switchChain,
  ]);

  return (
    <WalletsContext.Provider value={providerValue}>
      {children}
      {isAgeVerificationModalOpen && <AgeVerificationPopup onClose={resolve} />}
      {signingStatus === 'pending' && (<SignTermsPopup />)}
      {isWalletActionModalOpen && <WalletActionProcess title={walletActionProcessTitle} />}
      <PopUp />
    </WalletsContext.Provider>
  );
};

export const useWallets = () => {
  const context = useContext(WalletsContext);

  if (!context) {
    throw new Error('useWallets must be used within an WalletsProvider');
  }

  return context;
};
